import React, { useEffect, useMemo, useState } from "react";

import moment from "moment";

import { formatDate, useGetPracticeCrops, useTranslation } from "utils";

import { CheckboxChangeEvent, CheckboxSegmented, Flex, Form, Icon, Popover, Spin, Typography } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider.tsx";
import { CMC_ONBOARDING_1_ERRORS } from "pages/app/crop-mix-composer/CropMixComposerOverlay";
import { AddEditCropsFormValues } from "pages/app/crop-mix-composer/components/AddEditCropsSection.tsx";
import { MainCropSelectionForm } from "pages/app/crop-mix-composer/components/MainCropSelectionForm.tsx";

export const MainCropSelectionPopover = ({
  cropId,
  date,
  type,
  shouldShowErrorSpacer,
  disabled = false,
  loading = false,
}: {
  type: "firstCrop" | "secondCrop";
  cropId?: string;
  date?: string;
  shouldShowErrorSpacer?: boolean;
  disabled?: boolean;
  loading?: boolean;
}) => {
  const { t } = useTranslation("cropMixComposer");

  const { regionId, isChangingStep, onboarding1Errors, mainCropSettings } = useCropMixComposerContext();
  const { data: allCrops } = useGetPracticeCrops({ regionId });

  const [displayedCropId, setDisplayedCropId] = useState<string | undefined>(cropId);
  const [showPopover, setShowPopover] = useState<boolean>();
  const form = Form.useFormInstance<AddEditCropsFormValues>();

  const formDateValue = form.getFieldValue([type, "date"]);

  const cropsListToDisplay = useMemo(() => {
    if (!allCrops) return [];

    const enabledCrops = allCrops.filter((crop) => crop.enabled);
    if (!cropId) return enabledCrops;

    const selectedCrop = allCrops.find((crop) => crop.id === cropId);
    if (selectedCrop && !selectedCrop.enabled) {
      return [...enabledCrops, selectedCrop];
    }

    return enabledCrops;
  }, [allCrops, cropId]);

  // Derived on render so the name follows language changes without syncing crops into state.
  const cropName = allCrops?.find((crop) => crop.id === displayedCropId)?.name || t("selectCrop");

  const onSubmit = async () => {
    try {
      await form.validateFields();

      form.submit();

      setDisplayedCropId(form.getFieldValue([type, "cropId"]));
      setShowPopover(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const resetFields = () => {
    const previousCropId = type === "firstCrop" ? mainCropSettings?.crop1Id : type === "secondCrop" ? mainCropSettings?.crop2Id : undefined;
    const previousDate = type === "firstCrop" ? mainCropSettings?.harvestDate : type === "secondCrop" ? mainCropSettings?.seedingDate : undefined;

    form.setFieldValue([type, "cropId"], previousCropId);
    form.setFieldValue([type, "date"], previousDate ? moment(previousDate) : undefined);
    setDisplayedCropId(previousCropId);

    setShowPopover(false);
  };

  useEffect(() => {
    setDisplayedCropId(cropId);
  }, [cropId]);

  const handleClick = (e: CheckboxChangeEvent) => {
    e.stopPropagation();

    if (disabled || loading) {
      setShowPopover(false);
      return;
    }

    setShowPopover((prevState) => !prevState);
  };

  const getErrorMessage = () => {
    if (type === "firstCrop") {
      if (onboarding1Errors.includes(CMC_ONBOARDING_1_ERRORS.MAIN_CROP_1)) {
        return t("errors.addMissingData");
      }
    }

    if (type === "secondCrop") {
      if (onboarding1Errors.includes(CMC_ONBOARDING_1_ERRORS.MAIN_CROP_2)) {
        return t("errors.addMissingData");
      }
      if (onboarding1Errors.includes(CMC_ONBOARDING_1_ERRORS.SEEDING_BEFORE_HARVEST)) {
        return t("errors.seedingBeforeHarvest");
      }
      if (onboarding1Errors.includes(CMC_ONBOARDING_1_ERRORS.MINIMUM_GROWING_PERIOD)) {
        return t("errors.minimumGrowingPeriod");
      }
    }
    return null;
  };

  const errorMessage = getErrorMessage();

  return (
    <Popover
      className="crop-mix-composer-main-crop-popover"
      title={
        <Flex gap={"0.5"} style={{ padding: "1rem" }}>
          <Typography variant="h5">{t("mainCropModal.title")}</Typography>
          <Icon className="pointer" iconName="tabler/x" color="black" onClick={onSubmit} />
        </Flex>
      }
      open={!isChangingStep && showPopover && !disabled && !loading}
      trigger="click"
      onOpenChange={(visible) => {
        if (disabled || loading) setShowPopover(false);

        if (!visible) {
          resetFields();
        }
      }}
      destroyOnHidden={true}
      content={
        loading ? (
          <Flex justify="center" align="center" style={{ padding: "2rem" }}>
            <Spin size="large" />
          </Flex>
        ) : (
          <MainCropSelectionForm
            description={t("mainCropModal.description")}
            handleSubmit={onSubmit}
            type={type}
            cropId={cropId}
            date={date}
            availableCrops={cropsListToDisplay}
          />
        )
      }
    >
      <Flex vertical={true} justify="center" align="center" gap={"0.5rem"}>
        <div>
          <CheckboxSegmented
            key={`crop-mix-composer-main-crop-popover-button-${type}`}
            className={`crop-mix-composer-main-crop-popover-button ${errorMessage ? "error" : ""}`}
            onChange={handleClick}
            label={loading ? <Spin size="default" /> : cropName}
            disabled={disabled || loading}
          />
          {errorMessage ? (
            <Typography variant="paragraph-m" className="cmc-onboarding-step-error-message">
              {errorMessage}
            </Typography>
          ) : (
            shouldShowErrorSpacer && <span className="cmc-onboarding-step-error-message" />
          )}
        </div>
        {date || formDateValue ? (
          <Typography variant="paragraph-s" className="margin-0 crop-date-text">
            {`${t(`mainCropModal.${type === "firstCrop" ? "harvest" : "seeding"}`)}: ${formatDate(date || formDateValue, "DD.MM.YYYY", moment.ISO_8601)}`}
          </Typography>
        ) : (
          <span className="crop-mix-composer-selection-spacer" />
        )}
      </Flex>
    </Popover>
  );
};
